/*
  theme.js: core for every page.
  - config: window.ThemeConfig (from snippets/business-config.liquid). No business value or text is hard-coded here.
  - money(): formats paise with shop.money_format, so market prices never get a hard-coded "₹".
  - pub/sub + DOM events on document: theme:cart:updated, theme:cart:add, theme:variant:change,
    theme:drawer:open / theme:drawer:close, theme:section:load (apps and custom code can listen).
  - components: <header-wrap>, <mega-menu>, <menu-drawer>, <announcement-bar>, <scroll-rail>, <predictive-search> (lazy).
  - small behaviours: footer accordions, country auto-submit, notice dismiss, tab bar, deferred custom code, quick add.
*/

/* ---------- config, money ---------- */
export const config = (() => {
  try { return JSON.parse(document.getElementById('ThemeConfig').textContent); } catch (e) { return {}; }
})();
// Liquid's t filter HTML-escapes text (e.g. ' → &#39;); decode the strings once so textContent shows them right
if (config.strings) {
  const ta = document.createElement('textarea');
  Object.keys(config.strings).forEach((k) => { ta.innerHTML = config.strings[k]; config.strings[k] = ta.value; });
}
window.ThemeConfig = config;

export function money(paise, storeFormat = config.moneyFormat || '{{amount}}') {
  // the store format may wrap prices in HTML (e.g. <span class=money> for currency apps); JS output is text
  const format = String(storeFormat).replace(/<[^>]*>/g, '');
  const cents = Number(paise) || 0;
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const fixed = (n / 100).toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? w + decimal + frac : w;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return fmt(cents, 2);
      case 'amount_no_decimals': return fmt(cents, 0);
      case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return fmt(cents, 0, ' ');
      default: return fmt(cents, 2);
    }
  }).replace(/\.00(?=\D*$)/, '');
}

/* simple {{ token }} replacement for strings from config */
export function t(str = '', vars = {}) {
  return String(str).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));
}

/* ---------- pub/sub ---------- */
const subs = {};
export function subscribe(name, fn) { (subs[name] ||= []).push(fn); return () => { subs[name] = subs[name].filter((f) => f !== fn); }; }
export function publish(name, detail) {
  (subs[name] || []).forEach((fn) => fn(detail));
  document.dispatchEvent(new CustomEvent(`theme:${name}`, { detail }));
}

/* ---------- fetch helpers ---------- */
export async function fetchJSON(url, options = {}) {
  const res = await fetch(url, { ...options, headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.description || data.message || res.statusText), { status: res.status, data });
  return data;
}
export async function fetchSection(url, sectionId) {
  const u = new URL(url, location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u);
  if (!res.ok) throw new Error(res.statusText);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}

/* ---------- toast ---------- */
let toastTimer;
export function toast(message) {
  const el = document.querySelector('[data-toast]');
  if (!el) return;
  el.textContent = message;
  el.classList.add('is-open');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-open'), 2800);
}

/* ---------- drawers (focus trap, Esc, scrim, scroll lock) ---------- */
const scrim = document.querySelector('[data-scrim]');
let openDrawer = null;
let lastFocus = null;

export function openPanel(panel, trigger) {
  if (!panel) return;
  if (openDrawer && openDrawer !== panel) closePanel(openDrawer, false);
  lastFocus = trigger || document.activeElement;
  openDrawer = panel;
  panel.classList.add('is-open');
  scrim?.classList.add('is-open');
  document.body.classList.add('u-lock');
  document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  setTabbar(false);
  requestAnimationFrame(() => (panel.querySelector('[data-autofocus]') || panel).focus({ preventScroll: true }));
  publish('drawer:open', { id: panel.id });
}
export function closePanel(panel = openDrawer, restore = true) {
  if (!panel) return;
  panel.classList.remove('is-open');
  scrim?.classList.remove('is-open');
  document.body.classList.remove('u-lock');
  document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
  if (openDrawer === panel) openDrawer = null;
  setTabbar(true);
  if (restore && lastFocus) lastFocus.focus({ preventScroll: true });
  publish('drawer:close', { id: panel.id });
}
scrim?.addEventListener('click', () => closePanel());
document.addEventListener('keydown', (e) => {
  if (!openDrawer) return;
  if (e.key === 'Escape') { e.preventDefault(); closePanel(); return; }
  if (e.key !== 'Tab') return;
  const f = [...openDrawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), summary')]
    .filter((el) => el.offsetParent !== null);
  if (!f.length) return;
  const first = f[0], last = f[f.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
});
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-drawer-close]')) closePanel();
});

/* swipe-down to close bottom sheets */
export function enableSwipeClose(panel) {
  let y0 = null;
  panel.addEventListener('touchstart', (e) => {
    const body = panel.querySelector('.c-drawer__body');
    if (body && body.contains(e.target) && body.scrollTop > 0) return;
    y0 = e.touches[0].clientY;
  }, { passive: true });
  panel.addEventListener('touchmove', (e) => {
    if (y0 == null) return;
    const dy = e.touches[0].clientY - y0;
    if (dy > 0 && matchMedia('(max-width: 899.98px)').matches) panel.style.transform = `translateY(${dy}px)`;
  }, { passive: true });
  panel.addEventListener('touchend', (e) => {
    if (y0 == null) return;
    const dy = e.changedTouches[0].clientY - y0;
    panel.style.transform = '';
    y0 = null;
    if (dy > 90) closePanel(panel);
  });
}

/* tab bar is hidden while a sheet or the sticky ATC bar is showing */
const tabHolds = new Set();
export function setTabbar(show, key = 'drawer') {
  if (show) tabHolds.delete(key); else tabHolds.add(key);
  document.querySelector('[data-tabbar]')?.classList.toggle('is-hidden', tabHolds.size > 0);
}

/* ---------- header: sticky on scroll up, measured height ---------- */
class HeaderWrap extends HTMLElement {
  connectedCallback() {
    const mode = this.dataset.sticky;
    const setH = () => document.documentElement.style.setProperty('--header-h', mode === 'none' ? '0px' : `${this.offsetHeight}px`);
    setH();
    new ResizeObserver(setH).observe(this);
    // --header-offset: the part of the header on screen right now (0 once it has scrolled or slid away), for sticky
    // things that sit under it (subcategory sidebar)
    const root = document.documentElement;
    let lastOff = -1;
    const setOff = () => {
      const off = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      if (off !== lastOff) { lastOff = off; root.style.setProperty('--header-offset', off + 'px'); }
    };
    let offTick = false;
    window.addEventListener('scroll', () => {
      if (offTick) return;
      offTick = true;
      requestAnimationFrame(() => { setOff(); offTick = false; });
    }, { passive: true });
    // while the header section slides in or out, follow it every frame so nothing under it jumps at the end
    const box = this.parentElement || this;
    let sliding = false;
    const follow = () => { setOff(); if (sliding) requestAnimationFrame(follow); };
    box.addEventListener('transitionrun', (e) => { if (e.target === box && !sliding) { sliding = true; requestAnimationFrame(follow); } });
    box.addEventListener('transitionend', (e) => { if (e.target === box) { sliding = false; setOff(); } });
    box.addEventListener('transitioncancel', (e) => { if (e.target === box) { sliding = false; setOff(); } });
    window.addEventListener('resize', setOff, { passive: true });
    window.addEventListener('load', setOff, { once: true });
    setOff();
    if (mode !== 'scroll_up') return;
    let last = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        // act only on a real move (scroll events also fire without one, e.g. when sticky things below shift)
        if (Math.abs(y - last) < 4) { ticking = false; return; }
        const down = y > last && y > this.offsetHeight * 2;
        if (!this.contains(document.activeElement) && !this.querySelector('details[open]')) this.classList.toggle('is-hidden', down);
        last = y;
        ticking = false;
      });
    }, { passive: true });
  }
}
customElements.define('header-wrap', HeaderWrap);

/* ---------- mega menu: hover (desktop pointer), click, Esc, one open at a time ---------- */
class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    const hover = matchMedia('(hover: hover)').matches;
    let timer;
    if (hover) {
      this.addEventListener('mouseenter', () => { clearTimeout(timer); timer = setTimeout(() => this.open(), 80); });
      this.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => this.close(), 160); });
    }
    this.details.addEventListener('toggle', () => {
      this.summary.setAttribute('aria-expanded', this.details.open);
      if (this.details.open) document.querySelectorAll('mega-menu').forEach((m) => m !== this && m.close());
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.details.open) { this.close(); this.summary.focus(); } });
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.close(); });
  }
  open() { this.details.open = true; }
  close() { this.details.open = false; }
}
customElements.define('mega-menu', MegaMenu);
document.addEventListener('click', (e) => {
  if (!e.target.closest('mega-menu')) document.querySelectorAll('mega-menu').forEach((m) => m.close());
});

/* ---------- phone menu drawer ---------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-menu-open]');
      if (opener) { e.preventDefault(); this.openAt(opener.dataset.menuTarget, opener); }
    });
  }
  openAt(target, trigger) {
    // the menu list ships as a <template> (smaller page); build it the first time the drawer opens
    const tpl = this.querySelector('template[data-menu-nav]');
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
    if (target) {
      const item = this.querySelector(`details[data-menu-item="${target}"]`);
      if (item) item.open = true;
    }
    openPanel(this, trigger);
  }
}
customElements.define('menu-drawer', MenuDrawer);

/* ---------- announcement bar: rotate + dismiss ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const key = `announce-closed-${this.dataset.id}`;
    if (this.dataset.dismissible === 'true' && safeGet(sessionStorage, key)) { this.hidden = true; return; }
    this.querySelector('[data-dismiss]')?.addEventListener('click', () => { this.hidden = true; safeSet(sessionStorage, key, '1'); });
    const phoneRotate = this.classList.contains('s-announce--rotate-m') && matchMedia('(max-width: 899px)').matches;
    if (!this.classList.contains('s-announce--rotate') && !phoneRotate) return;
    // phone rotation cycles the messages meant for phones; full rotation those for the current screen
    const phone = matchMedia('(max-width: 899px)').matches;
    const cls = phoneRotate ? 'is-active-m' : 'is-active';
    const items = [...this.querySelectorAll('.s-announce__item')].filter((el) => !el.classList.contains(phone ? 'u-desktop-only' : 'u-mobile-only'));
    if (items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = Math.max(0, items.findIndex((el) => el.classList.contains(cls)));
    items.forEach((el, n) => el.classList.toggle(cls, n === i));
    const ms = (Number(this.dataset.interval) || 5) * 1000;
    let timer = setInterval(() => step(), ms);
    const step = () => { items[i].classList.remove(cls); i = (i + 1) % items.length; items[i].classList.add(cls); };
    this.addEventListener('mouseenter', () => clearInterval(timer));
    this.addEventListener('mouseleave', () => { clearInterval(timer); timer = setInterval(step, ms); });
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- scroll rail: arrows on desktop, lazy ---------- */
class ScrollRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    const update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    };
    const by = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.85, behavior: 'smooth' });
    this.prev?.addEventListener('click', () => by(-1));
    this.next?.addEventListener('click', () => by(1));
    this.track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(this.track);
    update();
  }
}
customElements.define('scroll-rail', ScrollRail);

/* ---------- safe storage ---------- */
export function safeGet(store, key) { try { return store.getItem(key); } catch (e) { return null; } }
export function safeSet(store, key, val) { try { store.setItem(key, val); } catch (e) { /* storage blocked */ } }

/* ---------- small behaviours ---------- */
function initFooter(root = document) {
  // footer columns: open on desktop, collapsed accordions on phone
  if (matchMedia('(max-width: 899.98px)').matches) root.querySelectorAll('[data-footer-col]').forEach((d) => { d.open = false; });
}
function initNoticeBars(root = document) {
  const bars = [...root.querySelectorAll('[data-notice-bar]')];
  bars.forEach((bar) => {
    if (!bar.hasAttribute('data-dismissible')) return;
    const key = `notice-closed-${bar.dataset.id}`;
    if (safeGet(localStorage, key)) { bar.dataset.closed = '1'; bar.hidden = true; return; }
    bar.querySelector('[data-dismiss]')?.addEventListener('click', () => { bar.dataset.closed = '1'; bar.hidden = true; safeSet(localStorage, key, '1'); });
  });
  // scheduled notices: hidden before their start, shown on time, removed at their end (checked every 15 s)
  const timed = bars.filter((b) => b.dataset.start || b.dataset.end);
  if (!timed.length) return;
  const check = () => {
    const now = Date.now();
    timed.forEach((bar) => {
      if (!bar.isConnected) return;
      const end = Number(bar.dataset.end) || 0;
      if (end && now >= end) { bar.remove(); return; }
      const start = Number(bar.dataset.start) || 0;
      bar.hidden = bar.dataset.closed === '1' || (start > 0 && now < start);
    });
    if (timed.every((b) => !b.isConnected)) clearInterval(timer);
  };
  const timer = setInterval(check, 15000);
  check();
}
function initSearchPlaceholder() {
  const mq = matchMedia('(max-width: 899.98px)');
  const apply = () => document.querySelectorAll('[data-ph-mobile]').forEach((input) => {
    input.dataset.phDesktop ||= input.placeholder;
    input.placeholder = mq.matches && input.dataset.phMobile ? input.dataset.phMobile : input.dataset.phDesktop;
  });
  apply();
  mq.addEventListener('change', apply);
  document.addEventListener('input', (e) => {
    const input = e.target.closest('.c-search__input');
    if (!input) return;
    const clear = input.parentElement.querySelector('[data-search-clear]');
    if (clear) clear.hidden = !input.value;
  });
  document.addEventListener('click', (e) => {
    const clear = e.target.closest('[data-search-clear]');
    if (!clear) return;
    const input = clear.parentElement.querySelector('.c-search__input');
    input.value = '';
    clear.hidden = true;
    input.focus();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}
document.addEventListener('change', (e) => {
  if (e.target.matches('select[data-autosubmit]')) e.target.form.submit();
});

/* tab bar actions */
document.addEventListener('click', (e) => {
  const tab = e.target.closest('[data-tab-action]');
  if (!tab) return;
  const action = tab.dataset.tabAction;
  if (action === 'search') {
    const input = document.querySelector('.c-search__input');
    if (input) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); document.querySelector('header-wrap')?.classList.remove('is-hidden'); input.focus(); }
  } else if (action === 'crafts' && tab.getAttribute('href') === '#MenuDrawer') {
    e.preventDefault();
    document.querySelector('menu-drawer')?.openAt(tab.dataset.menuTarget, tab);
  } else if (action === 'cart' && document.querySelector('cart-drawer')) {
    e.preventDefault();
    customElements.whenDefined('cart-drawer').then(() => document.querySelector('cart-drawer').open(tab));
  }
});

/* the pincode form only works with JS (shipping.js); never let it reload the page */
document.addEventListener('submit', (e) => {
  if (e.target.matches('[data-pincode-form]')) { e.preventDefault(); loadModule('shipping').then(() => e.target.closest('pincode-check')?.check?.(true)); }
});

/* header cart button opens the drawer when there is one */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-cart-open]');
  const drawer = document.querySelector('cart-drawer');
  if (btn && drawer) { e.preventDefault(); customElements.whenDefined('cart-drawer').then(() => drawer.open(btn)); }
});

/* deferred custom code (sections/custom-code.liquid) */
function runTemplate(tpl) {
  const frag = tpl.content.cloneNode(true);
  frag.querySelectorAll('script').forEach((old) => {
    const s = document.createElement('script');
    [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.textContent = old.textContent;
    old.replaceWith(s);
  });
  tpl.replaceWith(frag);
}
function initDeferredCode() {
  const tpls = [...document.querySelectorAll('template[data-deferred-code]')];
  if (!tpls.length) return;
  const byMode = (m) => tpls.filter((t) => t.dataset.deferredCode === m);
  const onLoad = () => byMode('load').forEach(runTemplate);
  if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad, { once: true });
  byMode('delay').forEach((tpl) => setTimeout(() => runTemplate(tpl), (Number(tpl.dataset.delay) || 5) * 1000));
  const inter = byMode('interaction');
  if (inter.length) {
    const evs = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    const go = () => { evs.forEach((ev) => window.removeEventListener(ev, go)); inter.forEach(runTemplate); };
    evs.forEach((ev) => window.addEventListener(ev, go, { passive: true, once: true }));
  }
}

/* ---------- quick add on cards (falls back to the form post without JS) ---------- */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-quick-add]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('[data-add-to-cart]');
  btn?.classList.add('is-loading');
  try {
    await addToCart(new FormData(form), btn);
  } finally {
    btn?.classList.remove('is-loading');
  }
});

export async function addToCart(formData, trigger, opts = {}) {
  const drawer = document.querySelector('cart-drawer');
  const sections = drawer?.sectionIds?.() || [];
  if (sections.length) formData.append('sections', sections.join(','));
  formData.append('sections_url', location.pathname);
  try {
    const res = await fetch(`${config.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || config.strings.error);
    publish('cart:add', { item: data, trigger });
    if (opts.silent) return data;
    if (drawer) {
      await customElements.whenDefined('cart-drawer');
      await drawer.renderFromAdd(data, trigger);
    } else if (config.cartType === 'page') {
      toast(config.strings.added);
      refreshCartCount();
    } else {
      location.href = config.routes.cart;
    }
    return data;
  } catch (err) {
    toast(err.message || config.strings.error);
    throw err;
  }
}

export async function refreshCartCount(cart) {
  const c = cart || await fetchJSON(`${config.routes.cart}.js`).catch(() => null);
  if (!c) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.dataset.count = c.item_count;
    el.textContent = c.item_count > 0 ? c.item_count : '';
  });
}
subscribe('cart:updated', (cart) => refreshCartCount(cart));

/* ---------- order limits (Theme settings › Offers and order rules) ----------
   Returns null when the cart is fine, else { type, message, blocking }. Used by the cart and Buy now. */
export function cartLimitProblem(cart) {
  const L = config.limits || {};
  if (!cart || !cart.item_count) return null;
  const msgs = L.messages || {};
  const blocking = !L.appMode;
  const total = cart.total_price;
  if (L.minOrder > 0 && total < L.minOrder) {
    return { type: 'min', blocking, message: (msgs.min || '').replace('[remaining]', money(L.minOrder - total)).replace('[min]', money(L.minOrder)) };
  }
  if (L.maxOrder > 0 && total > L.maxOrder) {
    return { type: 'max', blocking, message: (msgs.max || '').replace('[max]', money(L.maxOrder)) };
  }
  if (L.maxLineQty > 0) {
    const over = cart.items.find((i) => i.quantity > L.maxLineQty);
    if (over) return { type: 'line', blocking, key: over.key, message: (msgs.lineMax || '').replace('[qty]', L.maxLineQty) };
  }
  return null;
}

/* ---------- quantity stepper (product page, quick add, cart) ---------- */
class QtyStepper extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.down = this.querySelector('[data-step-down]');
    this.up = this.querySelector('[data-step-up]');
    this.down?.addEventListener('click', () => this.step(-1));
    this.up?.addEventListener('click', () => this.step(1));
    this.input?.addEventListener('change', () => this.setValue(Number(this.input.value), false));
    this.sync();
  }
  get min() { return Number(this.dataset.min) || 1; }
  get max() { return Number(this.dataset.max) || 0; }
  get stepSize() { return Number(this.dataset.step) || 1; }
  step(dir) { this.setValue(Number(this.input.value) + dir * this.stepSize); }
  setValue(v, fire = true) {
    let n = Math.max(this.min, Number.isFinite(v) ? v : this.min);
    if (this.max > 0) n = Math.min(n, this.max);
    const off = (n - this.min) % this.stepSize;
    if (off) n -= off;
    const changed = Number(this.input.value) !== n;
    this.input.value = n;
    this.sync();
    if (fire && changed) this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  sync() {
    const n = Number(this.input.value);
    if (this.down) this.down.disabled = n <= this.min;
    if (this.up) this.up.disabled = this.max > 0 && n >= this.max;
  }
}
if (!customElements.get('qty-stepper')) customElements.define('qty-stepper', QtyStepper);

/* quick add for products with options (cards) */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-view]');
  if (!btn) return;
  e.preventDefault();
  btn.classList.add('is-loading');
  loadModule('product').then(() => window.theme.openQuickView(btn.dataset.quickView, btn)).catch(() => { location.href = btn.href; }).finally(() => btn.classList.remove('is-loading'));
});

/* ---------- lazy modules (exact CDN URLs come from ThemeConfig.assets) ---------- */
const loaded = {};
export function loadModule(key) {
  const url = config.assets?.[key];
  if (!url) return Promise.resolve();
  const u = new URL(url, location.href);
  const src = u.origin === location.origin ? u.href : u.pathname + u.search;
  return (loaded[key] ||= import(src));
}
function lazy(selector, key) {
  const els = document.querySelectorAll(selector);
  if (!els.length) return;
  const io = new IntersectionObserver((entries) => {
    if (entries.some((en) => en.isIntersecting)) { io.disconnect(); loadModule(key); }
  }, { rootMargin: '300px' });
  els.forEach((el) => io.observe(el));
}

/* shared API for the other theme scripts (they load as separate modules; never import theme.js twice) */
window.theme = { cartLimitProblem, loadModule, config, money, t, subscribe, publish, fetchJSON, fetchSection, toast, openPanel, closePanel, enableSwipeClose, setTabbar, safeGet, safeSet, addToCart, refreshCartCount };

function init(root = document) {
  initFooter(root);
  initNoticeBars(root);
}

init();
initSearchPlaceholder();
initDeferredCode();
lazy('predictive-search[data-enabled="true"]', 'predictive');
// a tap into the search field before the script has loaded still opens the panel
document.addEventListener('focusin', (e) => {
  const input = e.target.closest?.('predictive-search[data-enabled="true"] .c-search__input');
  if (!input || customElements.get('predictive-search')) return;
  loadModule('predictive').then(() => customElements.whenDefined('predictive-search')).then(() => {
    if (document.activeElement === input) input.dispatchEvent(new FocusEvent('focus'));
  });
});
// approximate local prices (outside India) and the one-time "Shopping from X?" bar
if (config.converter?.mode === 'builtin' || (config.converter?.suggest && !safeGet(sessionStorage, 'country-suggest'))) {
  const goFx = () => loadModule('currency');
  ('requestIdleCallback' in window) ? requestIdleCallback(goFx, { timeout: 2500 }) : setTimeout(goFx, 800);
}
// shipping messages matter on first view and appear again inside the re-rendered cart, so load on idle (not on scroll)
if (document.querySelector('shipping-card, pincode-check, cart-progress, cart-drawer, cart-page')) {
  const go = () => loadModule('shipping');
  ('requestIdleCallback' in window) ? requestIdleCallback(go, { timeout: 1500 }) : setTimeout(go, 300);
}

/* theme editor: re-run small behaviours when a section reloads */
document.addEventListener('shopify:section:load', (e) => { init(e.target); publish('section:load', { id: e.detail.sectionId, target: e.target }); });
document.addEventListener('shopify:block:select', (e) => {
  const d = e.target.closest('details');
  if (d) d.open = true;
});

/* app compatibility: refresh the drawer when an app adds to cart with fetch */
const nativeFetch = window.fetch;
window.fetch = async function (...args) {
  const res = await nativeFetch.apply(this, args);
  try {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const fromTheme = args[1]?.headers && (args[1].headers['X-Requested-With'] === 'XMLHttpRequest');
    if (!fromTheme && /\/cart\/(add|change|update|clear)(\.js)?/.test(url) && res.ok) {
      setTimeout(() => document.querySelector('cart-drawer')?.refresh?.(), 50);
      setTimeout(() => refreshCartCount(), 60);
    }
  } catch (e) { /* ignore */ }
  return res;
};
